import React, { useState, useEffect } from 'react';
import '../Coaching/academyplayerlist.css';
import { useUser } from '../context/UserContext';
import { hasPrivilege } from '../utils/hasPrivilege';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Link, useLocation } from 'react-router-dom';
import Swal from "sweetalert2";
import axios from "axios";
import { FiChevronDown } from 'react-icons/fi';

const BatchPlayerList = () => {
    const [batchName, setBatchName] = useState('');
    const [searchParams] = useSearchParams();
    const batchId = searchParams.get("batchId");
    const { userData } = useUser();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [players, setPlayers] = useState([]);
    const token = localStorage.getItem('auth-token');
    useEffect(() => {
        const fetchPlayersData = async () => {
            if (!batchId) {
                setError('No batchId provided.');
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const url = `${process.env.REACT_APP_API_ADD_PLAYER_BATCH}/${batchId}/players`;


                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch players data');
                }

                const result = await response.json();

                if (result.success) {
                    const updatedPlayers = result.data.players.map((player) => {
                        const playerId = player.id;
                        return {
                            id: playerId,
                            user: player.user.id,
                            name: player.user.name || '-',
                            parentName: result.data.parentPlayer?.[playerId] || '-',
                            parentMobile: '-',
                            category: player.playerCategoryType || '-',
                            level: player.playerLevelType || '-',
                            mobile: player.user.mobile_number || '-',
                            dob: formatDate(player.dateOfBirth) || '-',
                            isEnrolled: result.data.isEnrollmentMap?.[playerId] || false,
                            playingMember: result.data.playingMember?.[playerId] || false,
                        };
                    });
                    if (result.data.batch?.name) {
                        setBatchName(result.data.batch.name);
                    }
                    setPlayers(updatedPlayers);
                }
                else {
                    setError('No players found.');
                }
            } catch (error) {

                setError('Error fetching data: ' + error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchPlayersData();
    }, [batchId]);


    const formatDate = (dateString) => {
        if (!dateString || isNaN(Date.parse(dateString))) {
            return '-';
        }
        const dateOnly = dateString.split('T')[0];

        return dateOnly;
    };

    const headers = [
        { label: "Name", key: "name" },
        { label: "Parent Name", key: "parentName" },
        { label: "Category", key: "category" },
        { label: "Level", key: "level" },
        { label: "Date of Birth", key: "dob" },
    ];

    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setrowsPerPage] = useState(10);
    const [formData, setFormData] = useState({
        name: '',
        parentName: '',
        parentMobile: '',
        category: '',
        level: '',
        mobile: '',
        dob: '',
        isEnrolled: false,
        playingMember: false,
    });

    const [dropdownOpen, setDropdownOpen] = useState(null);

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: name === 'isEnrolled' || name === 'playingMember' ? e.target.checked : value });
    };

    const filteredPlayers = players.filter((player) =>
        Object.values(player)
            .join(' ')
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
    );

    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

    const toggleDropdown = (playerId, e) => {
        e.stopPropagation();
        if (dropdownOpen === playerId) {
            return setDropdownOpen(null);
        }
        const { bottom, left } = e.currentTarget.getBoundingClientRect();
        setDropdownPos({
            top: bottom + window.scrollY,
            left: left + window.scrollX,
        });
        setDropdownOpen(playerId);
    };

    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const [sortConfig, setSortConfig] = useState({
        key: "name",
        direction: "ascending",
    });

    const handleSort = (columnKey) => {
        let direction = "ascending";
        if (sortConfig.key === columnKey && sortConfig.direction === "ascending") {
            direction = "descending";
        }
        setSortConfig({ key: columnKey, direction });
    };

    const sortData = (data) => {
        const { key, direction } = sortConfig;
        return [...data].sort((a, b) => {
            if (a[key] < b[key]) return direction === "ascending" ? -1 : 1;
            if (a[key] > b[key]) return direction === "ascending" ? 1 : -1;
            return 0;
        });
    };

    const sortedData = sortData(filteredPlayers);
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(sortedData.length / rowsPerPage);

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
    };

    const handlePrevPage = () => {
        if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
    };

    const generatePageNumbers = () => {
        const pages = [];
        const totalPagesToShow = 5;

        if (totalPages <= totalPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    const renderPagination = () => {
        return generatePageNumbers().map((page, index) =>
            typeof page === 'number' ? (
                <button
                    key={index}
                    className={`page-number ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageClick(page)}
                >
                    {page}
                </button>
            ) : (
                <span key={index} className="pagination-dots">
                    {page}
                </span>
            )
        );
    };
    useEffect(() => {
        const closeOnScroll = () => setDropdownOpen(null);

        window.addEventListener('scroll', closeOnScroll, true);
        return () => window.removeEventListener('scroll', closeOnScroll, true);
    }, []);
    useEffect(() => {
        const handleClickOutside = (event) => {
            const dropdown = document.querySelector('.custom-dropdown-player');
            if (dropdown && !dropdown.contains(event.target) && !event.target.closest('.dots-btn-player')) {
                setDropdownOpen(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    const handleCreateplayer = (event) => {
        const url = `/addPlayerBatch?batchId=${batchId}`;

        if (event?.metaKey || event?.ctrlKey) {
            window.open(url, "_blank"); 
        } else {
            navigate(url);
        }
    };


    const cancelAction = () => {
        setIsModalOpen(false);
    };

    const handleItemsPerPageChange = (event) => {
        setrowsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };
    const totalEntries = filteredPlayers.length;
    const indexOfFirstEntry = (currentPage - 1) * rowsPerPage + 1;
    const indexOfLastEntry = Math.min(currentPage * rowsPerPage, totalEntries);

    const handlePlayerDelete = async (playerId, batchId) => {
        Swal.fire({
            title: "Are you sure?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await axios.get(
                        `${process.env.REACT_APP_API_REMOVE_FROM_NEW}?batch_id=${batchId}&player_id=${playerId}`,
                        {
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`,
                            },
                        }
                    );

                    const { success, success_message, data } = response.data;
                    const message = success
                        ? data?.successMessage || success_message || "Player removed successfully!"
                        : data?.errorMessage || success_message || "Something went wrong.";

                    Swal.fire({
                        icon: success ? "success" : "error",
                        title: success ? "Success" : "Error",
                        text: message,
                        toast: true,
                        position: "top-end",
                        showConfirmButton: false,
                        timer: 3000,
                    });
                    if (data?.successMessage) {
                        setPlayers((prevPlayers) => prevPlayers.filter(player => player.id !== playerId));
                    }
                } catch (error) {

                    Swal.fire({
                        icon: "error",
                        title: "Failed",
                        text: "Could not remove the player.",
                        toast: true,
                        position: "top-end",
                        showConfirmButton: false,
                        timer: 3000,
                    });
                }
            }
        });
    };
    const handleNavigate = (event, url) => {
        if (event.metaKey || event.ctrlKey) {
            window.open(url, "_blank");
        } else {
            navigate(url);
        }
    };

    return (
        <div className="enrollment-container">

            <div className="search-bar">
                <div className="input-container">
                    <input
                        type="text"
                        id="searchInput"
                        placeholder=""
                        className={`search-input ${searchTerm ? "has-value" : ""}`}
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                    <label htmlFor="searchInput" className="floating-label">
                        🔍 Search
                    </label>
                </div>
                <button
                    className="create-button-new"
                    onClick={(e) => handleCreateplayer(e)}
                >
                    <span className="create-button__icon">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            width="23"
                            height="23"
                            className="create-button__svg"
                        >
                            <path
                                d="M12 5v14M5 12h14"
                                stroke="white"
                                strokeWidth="2.3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </span>
                    <span className="create-button__label">
                        Add Player
                    </span>
                </button>

            </div>

            <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
                <option value={10}>10 items per page</option>
                <option value={50}>50 items per page</option>
                <option value={75}>75 items per page</option>
                <option value={100}>100 items per page</option>
            </select>


            {loading && (
                <div className="premium-loader">
                    <div className="progress-track">
                        <div className="progress-bar"></div>
                    </div>
                    <div className="status-text">
                        <span>L</span>
                        <span>O</span>
                        <span>A</span>
                        <span>D</span>
                        <span>I</span>
                        <span>N</span>
                        <span>G</span>
                    </div>
                </div>
            )}
            {error && <div className="error-message">{error}</div>}
            {!loading && !error && filteredPlayers.length === 0 && (
                <div className="error-message">No data available</div>
            )}
            <div className="items-and-dues">
                <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
                    <option value={10}>10 items per page</option>
                    <option value={50}>50 items per page</option>
                    <option value={75}>75 items per page</option>
                    <option value={100}>100 items per page</option>
                </select>
                <div className="pagination-selector">
                    <div className="button-group">
                        <div className="group-label">Items per page:</div>
                        <div className="button-options">
                            {[10, 50, 75, 100].map((option) => (
                                <button
                                    key={option}
                                    onClick={() =>
                                        handleItemsPerPageChange({ target: { value: option } })
                                    }
                                    className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                                >
                                    <span className="button-text">{option}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                {batchName && (
                    <div style={{ padding: '10px 0', fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginTop: '0px' }}>
                        📋 Batch: {batchName}
                    </div>
                )}
            </div>
            <div className="payment-list-scroll-container" style={{ overflowY: 'auto' }}>

                <table className="payment-list-table">
                    <thead>
                        <tr>
                            {headers.map(({ key, label }) => (
                                <th key={key} className="challenges-column" onClick={() => handleSort(key)}>
                                    <div className="th-content">
                                        <span>{label}</span>
                                        <FiChevronDown
                                            className={`sort-icon ${sortConfig.key === key ? 'active' : ''
                                                } ${sortConfig.key === key && sortConfig.direction === 'descending' ? 'desc' : ''}`}
                                        />
                                    </div>
                                </th>
                            ))}
                            {hasPrivilege(userData, 'COACHING', 'ACADEMY_PLAYER_LIST_MODIFY') && (<th>Action</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {currentRows.map((player) => (
                            <tr key={player.id}>
                                <td>{player.name}</td>
                                <td>{player.parentName}</td>
                                <td>{player.category}</td>
                                <td>{player.level}</td>
                                <td>{player.dob}</td>
                                {hasPrivilege(userData, 'COACHING', 'ACADEMY_PLAYER_LIST_MODIFY') && (
                                    <td className="sport-actions-column">
                                        <button
                                            className="dots-btn-player"
                                            onClick={(e) => toggleDropdown(player.id, e)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: '24px',
                                            }}
                                        >
                                            &#x22EE;
                                        </button>

                                        {dropdownOpen === player.id && (
                                            <div
                                                className="custom-dropdown-player"
                                                style={{
                                                    position: 'absolute',
                                                    top: dropdownPos.top,
                                                    left: dropdownPos.left,
                                                    zIndex: 9999,
                                                    backgroundColor: 'white',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '4px',
                                                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                                                    minWidth: '200px',
                                                }}
                                            >
                                                <button
                                                    className="dropdown-item"
                                                    onClick={(event) => handleNavigate(event, `/playerAttendance?playerId=${player.id}&batchId=${batchId}`)}
                                                >
                                                    Attendance
                                                </button>

                                                <button
                                                    className="dropdown-item"
                                                    onClick={(event) => handleNavigate(event, `/performance?playerId=${player.id}&batchId=${batchId}`)}
                                                >
                                                    Performance
                                                </button>
                                                <button
                                                    className="dropdown-item"
                                                    onClick={() => handlePlayerDelete(player.id, batchId)}
                                                >
                                                    Remove Player
                                                </button>
                                                <button
                                                    className="dropdown-item"
                                                    onClick={(event) => handleNavigate(event, `/playerpaymentDetail?playerId=${player.id}&batchId=${batchId}`)}
                                                >
                                                    Update Player Payment Details
                                                </button>


                                            </div>
                                        )}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="pagination">
                <div className="showing-entries">
                    Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
                </div>
                <button
                    className="prev-button"
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                >
                    Previous
                </button>
                {renderPagination()}
                <button
                    className="next-button"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                >
                    Next
                </button>
            </div>

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button className="close-btn" onClick={cancelAction}>&times;</button>
                        <div>
                            <p>
                                Are you sure you want to delete?
                            </p>
                            <div className="modal-actions">
                                <button className="confirm-btn-academy" >
                                    Confirm Disable
                                </button>
                                <button className="cancel-btn-academy" onClick={cancelAction}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BatchPlayerList
